// src/utils/makeStylesSafe.js
import { makeStyles as muiMakeStyles, styled as muiStyled } from "@mui/styles";

/**
 * Safe makeStyles wrapper that ensures theme is properly defined
 */
export const makeStyles = (stylesOrCreator) => {
  return muiMakeStyles((theme = {}) => {
    // Ensure theme has ALL required properties with fallbacks
    const safeTheme = {
      palette: theme?.palette || {
        primary: {
          main: "#d66233",
          dark: "#b84d24",
          light: "#ff9041",
          darker: "#9a3d1e",
          lighter: "#ffb366",
        },
        secondary: { main: "#7871E1", dark: "#5d55b3", light: "#9d97e9" },
        error: { main: "#ff4842" },
        warning: { main: "#ffc107" },
        info: { main: "#00B0CF" },
        success: { main: "#78c36c" },
        grey: {
          500_12: "rgba(145, 158, 171, 0.12)",
          500: "#919EAB",
        },
        common: { white: "#fff", black: "#000" },
        background: {
          default: "#fff",
          paper: "#fff",
          neutral: "#f4f6f8",
        },
        text: {
          primary: "#212B36",
          secondary: "#637381",
          disabled: "#919EAB",
        },
      },
      spacing:
        theme?.spacing ||
        ((value) => {
          if (typeof value === "number") {
            return `${value * 8}px`;
          }
          if (Array.isArray(value)) {
            return value.map((v) => `${v * 8}px`).join(" ");
          }
          return value;
        }),
      shape: theme?.shape || {
        borderRadius: 8,
        borderRadiusSm: 12,
        borderRadiusMd: 16,
      },
      breakpoints: theme?.breakpoints || {
        up: (key) => {
          const values = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 };
          return `@media (min-width: ${values[key] || 600}px)`;
        },
        down: (key) => {
          const values = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 };
          // down() should use max-width with the breakpoint value minus a small amount
          return `@media (max-width: ${(values[key] || 600) - 0.05}px)`;
        },
        between: (start, end) => {
          const values = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 };
          return `@media (min-width: ${values[start] || 600}px) and (max-width: ${(values[end] || 960) - 0.05}px)`;
        },
        values: {
          xs: 0,
          sm: 600,
          md: 960,
          lg: 1280,
          xl: 1920,
        },
      },
      transitions: theme?.transitions || {
        create: (props, options) => "all 0.3s ease",
        duration: {
          shortest: 150,
          shorter: 200,
          short: 250,
          standard: 300,
          complex: 375,
          enteringScreen: 225,
          leavingScreen: 195,
        },
        easing: {
          easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
          easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
          easeIn: "cubic-bezier(0.4, 0, 1, 1)",
          sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
        },
      },
      shadows: theme?.shadows || Array(25).fill("none"),
      customShadows: theme?.customShadows || {
        z1: "0 1px 2px 0 rgba(0,0,0,0.05)",
        z8: "0 8px 16px 0 rgba(0,0,0,0.1)",
        z12: "0 12px 24px -4px rgba(0,0,0,0.12)",
        z16: "0 16px 32px -4px rgba(0,0,0,0.16)",
        z20: "0 20px 40px -4px rgba(0,0,0,0.20)",
        z24: "0 24px 48px 0 rgba(0,0,0,0.20)",
        primary: "0 8px 16px 0 rgba(214, 98, 51, 0.24)",
        info: "0 8px 16px 0 rgba(0, 176, 207, 0.24)",
        success: "0 8px 16px 0 rgba(120, 195, 108, 0.24)",
        warning: "0 8px 16px 0 rgba(255, 193, 7, 0.24)",
        error: "0 8px 16px 0 rgba(255, 72, 66, 0.24)",
      },
      typography: theme?.typography || {
        fontFamily: '"Outfit", "Geologica", sans-serif',
      },
      zIndex: theme?.zIndex || {
        mobileStepper: 1000,
        speedDial: 1050,
        appBar: 1100,
        drawer: 1200,
        modal: 1300,
        snackbar: 1400,
        tooltip: 1500,
      },
    };

    // Call the styles function or return the styles object
    if (typeof stylesOrCreator === "function") {
      return stylesOrCreator(safeTheme);
    }
    return stylesOrCreator;
  });
};


export const styled = (component) => {
  return (styles) => {
    // If styles is a function, wrap it to provide safe theme
    if (typeof styles === "function") {
      return muiStyled(component)((theme = {}) => {
        // Create a safe spacing function
        const spacing = theme?.spacing || ((value) => {
          if (typeof value === "number") {
            return `${value * 8}px`;
          }
          if (Array.isArray(value)) {
            return value.map((v) => `${v * 8}px`).join(" ");
          }
          return value;
        });

        // Call the original styles function with enhanced theme
        return styles({ ...theme, spacing });
      });
    }
    
    // If styles is an object, just pass it through
    return muiStyled(component)(styles);
  };
};

export default makeStyles;